import * as React from 'react';

import { Switch, Route, BrowserRouter as Router, withRouter, Redirect } from 'react-router-dom';
import { Provider } from 'react-redux';

import { get, isEmpty } from 'lodash';
import { compose, lifecycle, withProps } from 'recompose';
import { Notifs } from 'redux-notifications';
import { withNotifications } from '@components/hoc/withNotifications';
import HTML5Backend from 'react-dnd-html5-backend';
import { DragDropContextProvider } from 'react-dnd';

import ThemeContainer from '@inqool/uas-web-core/es/components/app/ThemeContainer';

import { ConfigurationProvider } from '@components/provider/configuration';
import { withConfiguration } from '@components/hoc/withConfiguration';
import { setSettings, getConfig } from '@components/provider/configuration/_configurationActions';
import { Sidebar } from '@components/sidebar';
import { configurationValues } from '@components/provider/configuration/_configurationConstants';
import { LoginForm } from '@modules/login/LoginForm';
import { ChangePasswordPage } from '@modules/login/ChangePasswordPage';
import { LogoutPage } from '@modules/logout/LogoutPage';
import { isLoggedIn, kerberosSignIn } from '@modules/login/_loginActions';
import {
  isPasswordChangeExtensionEnabled,
  isPasswordChangeRequired,
  PASSWORD_CHANGE_PATH,
  shouldRedirectToPasswordChange
} from '@modules/login/_passwordChange';
import { getGeneralSettings } from '@modules/generalSettings/_generalSettingsActions';
import { renewToken } from '@utils/actions';
import { routes } from './routes';

import { getTheme } from './theme';
import './styles/style.css';
import 'leaflet/dist/leaflet.css';
import { ConfigurationConsumer } from '@components/provider/configuration/ConfigurationProvider';
import { filterByModulesAndPermissions } from '@utils/utils';
import { getColumnSettings, postColumnSettings } from '@modules/columnSettings/_columnSettingsActions';
import useColumnSettings from '@modules/columnSettings/_columnSettingsStore';

const AuthRouteBase = props => {
  if (
    shouldRedirectToPasswordChange(
      isPasswordChangeRequired(),
      props.location.pathname,
      isPasswordChangeExtensionEnabled(get(props, 'config.extensions', {}))
    )
  ) {
    return <Redirect to={PASSWORD_CHANGE_PATH} />;
  }

  return props.children;
};

const AuthRoute = compose(
  withRouter,
  withConfiguration({ setSettings }),
  withNotifications({ kerberosSignIn, getGeneralSettings, getColumnSettings, postColumnSettings }),
  withProps(() => {
    const { setColumnSettingsId, deserializeColumnSettings, syncColumnSettings } = useColumnSettings();
    return { setColumnSettingsId, deserializeColumnSettings, syncColumnSettings };
  }),
  lifecycle<any, any, any>({
    async componentDidMount() {
      const {
        history,
        addSuccess,
        config,
        kerberosSignInNotify,
        getGeneralSettingsNotify,
        getColumnSettingsNotify,
        setSettingsConfig,
        setColumnSettingsId,
        deserializeColumnSettings,
        syncColumnSettings
      } = this.props;
      if (!isLoggedIn()) {
        if (get(history, 'location.pathname') !== '/login') {
          history.push('/login');
        }
        if (get(config, 'extensions.login', null) === configurationValues.config.extensions.login.kerberosLdap) {
          addSuccess('Probíhá přihlašování');
          kerberosSignInNotify(isPasswordChangeExtensionEnabled(get(config, 'extensions', {})));
        }
      } else if (isEmpty(get(config, 'settings'))) {
        const generalSettings = await getGeneralSettingsNotify();
        setSettingsConfig(generalSettings.otherSettings);
      }
      const { id, settings } = await getColumnSettingsNotify();
      setColumnSettingsId(id);
      if (id && settings && settings !== '') {
        const deserializedSettings = deserializeColumnSettings(settings);
        syncColumnSettings(deserializedSettings);
      }
    }
  })
)(AuthRouteBase);

const enhance = compose<any, any>(
  withNotifications({ getConfig }),
  lifecycle<any, {}, {}>({
    async componentDidMount() {
      const { getConfigNotify } = this.props;
      try {
        const config = await getConfigNotify();
        this.setState({ config });
        const isKeepaliveDisabled = get(config, 'extensions.keepalive-disabled') === 'true';
        if (!isKeepaliveDisabled) {
          renewToken();
        }
      } catch {
        this.setState({ config: {} });
      }
    }
  })
);

const View: React.SFC<any> = ({ store }) => (
  <DragDropContextProvider backend={HTML5Backend}>
    <Provider store={store}>
      <div>
        <Notifs />
        <ConfigurationProvider>
          <div>
            <Router>
              <ConfigurationConsumer>
                {({ config }) => {
                  const filteredRoutes = filterByModulesAndPermissions(config.modules, routes);

                  // const theme = getTheme(!!config['high-contrast']);
                  const theme = getTheme();

                  return (
                    <ThemeContainer theme={theme}>
                      <Switch>
                        <Route exact={true} path="/login" component={LoginForm} />
                        <Route exact={true} path="/logout" component={LogoutPage} />
                        <Route exact={true} path={PASSWORD_CHANGE_PATH} component={ChangePasswordPage} />
                        <AuthRoute>
                          <Sidebar className="mp-sidebar">
                            {filteredRoutes.map(o => <Route key={o.path} exact={true} {...o} />)}
                          </Sidebar>
                        </AuthRoute>
                        <Route component={() => <Redirect to="/" />} />
                      </Switch>
                    </ThemeContainer>
                  );
                }}
              </ConfigurationConsumer>
            </Router>
          </div>
        </ConfigurationProvider>
      </div>
    </Provider>
  </DragDropContextProvider>
);

export default enhance(View);



// WEBPACK FOOTER //
// ./src/App.tsx